'use client'

import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { CATALOG_STALE_TIME, PRODUCT_LIST_PAGE_SIZE } from '@/constants/catalog'
import { type IProductFilters } from '@/schemas/catalog/productFilters'
import { listProductsService } from '@/services/domain/catalog/ProductListService'

interface IUseProductsOptions {
    filters: IProductFilters
    pageSize?: number
}

/**
 * Listagem `/produtos` com scroll infinito: páginas no cache do React Query por filtro (query key
 * `['catalog-products', filtros, tamanho]`). Ao trocar o filtro, a lista anterior continua na tela até a nova chegar
 * (`isPlaceholderData`). Há mais páginas enquanto a última veio cheia (a API não devolve total).
 */
export function useProducts({ filters, pageSize = PRODUCT_LIST_PAGE_SIZE }: IUseProductsOptions) {
    const query = useInfiniteQuery({
        queryKey: ['catalog-products', filters, pageSize],
        queryFn: ({ pageParam }) => listProductsService({ filters, page: pageParam, pageSize }),
        staleTime: CATALOG_STALE_TIME,
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => (lastPage.length === pageSize ? allPages.length : undefined),
        placeholderData: keepPreviousData,
    })

    const products = useMemo(() => query.data?.pages.flat() ?? [], [query.data])

    const { hasNextPage, isFetchingNextPage, fetchNextPage } = query
    const loadMore = useCallback(async () => {
        if (hasNextPage && !isFetchingNextPage) await fetchNextPage()
    }, [hasNextPage, isFetchingNextPage, fetchNextPage])

    return {
        products,
        hasMore: Boolean(hasNextPage),
        loadMore,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isFetchingNextPage,
        isPlaceholderData: query.isPlaceholderData,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    }
}
